import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Pagination from "@cloudscape-design/components/pagination";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { chestPrizeStore } from "../stores/chestPrize";
import { chestPrizeAddModalStore } from "../stores/chestPrizeAddModal";
import { chestPrizeEditModalStore } from "../stores/chestPrizeEditModal";
import { formatChestPrizeType } from "../helpers/chestPrizeTypes";

const parseChestRewardConditions = (value, fallback = {}) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === "object") {
		return value;
	}

	if (typeof value === "string" && value.trim().length) {
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	}

	return fallback;
};

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions, []);

	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (Array.isArray(parsed?.prizes)) {
		return parsed.prizes;
	}

	return [];
};

class ChestPrize extends React.Component {
	componentDidMount() {
		this.props.Interface.loadChestPrizeList();
	}

	getRewardsSummary = (rewardConditions) => {
		const rewards = getChestRewardsPayload(rewardConditions);
		if (!rewards.length) {
			return "Empty";
		}

		const parts = rewards.map((reward) => reward.rewardType).filter(Boolean);
		return parts.length ? parts.join(", ") : "Configured";
	};

	openEditModal = (item) => {
		this.props.setChestPrizeEditModal({
			...this.props.chestPrizeEditModal,
			show: true,
			loaded: true,
			id: item.id,
			name: item.name || "",
			type: item.type || null,
			rewardConditions: item.rewardConditions || [],
			chestRewards: [],
		});
	};

	render() {
		const { chestPrize } = this.props;

		return (
			<>
				{chestPrize.error.text && (
					<Alert type="error" header="Error loading chests">
						{chestPrize.error.text}
					</Alert>
				)}

				<Table
					stickyHeader
					trackBy="id"
					loading={!chestPrize.loaded}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							cell: (item) => (
								<Button variant="normal" onClick={() => this.openEditModal(item)}>
									{item.id}
								</Button>
							),
						},
						{ id: "name", header: "Name", cell: (item) => item.name || "-" },
						{ id: "type", header: "Type", cell: (item) => formatChestPrizeType(item.type) },
						{
							id: "rewardCount",
							header: "Rewards",
							cell: (item) => getChestRewardsPayload(item.rewardConditions).length,
						},
						{
							id: "rewardSummary",
							header: "Summary",
							cell: (item) => this.getRewardsSummary(item.rewardConditions),
						},
					]}
					items={chestPrize.data}
					pagination={
						<Pagination
							currentPageIndex={chestPrize.pagination.current}
							pagesCount={chestPrize.pagination.total}
							onChange={({ detail }) => {
								this.props.setChestPrize({
									...chestPrize,
									pagination: { ...chestPrize.pagination, current: detail.currentPageIndex },
								});
								setTimeout(() => this.props.Interface.loadChestPrizeList(), 100);
							}}
						/>
					}
					header={
						<Header
							actions={
								<SpaceBetween direction="horizontal" size="xs">
								
									<Button
										variant="primary"
										onClick={() =>
											this.props.setChestPrizeAddModal({
												...this.props.chestPrizeAddModal,
												show: true,
											})
										}
									>
										Add chest
									</Button>
								</SpaceBetween>
							}
							counter={`(${chestPrize.pagination.count})`}
						>
							Chest catalog
						</Header>
					}
					empty={<Box textAlign="center">No chests found</Box>}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const chestPrize = useRecoilValue(chestPrizeStore);
		const setChestPrize = useSetRecoilState(chestPrizeStore);

		const chestPrizeAddModal = useRecoilValue(chestPrizeAddModalStore);
		const setChestPrizeAddModal = useSetRecoilState(chestPrizeAddModalStore);

		const chestPrizeEditModal = useRecoilValue(chestPrizeEditModalStore);
		const setChestPrizeEditModal = useSetRecoilState(chestPrizeEditModalStore);

		return (
			<Component
				{...props}
				chestPrize={chestPrize}
				setChestPrize={setChestPrize}
				chestPrizeAddModal={chestPrizeAddModal}
				setChestPrizeAddModal={setChestPrizeAddModal}
				chestPrizeEditModal={chestPrizeEditModal}
				setChestPrizeEditModal={setChestPrizeEditModal}
			/>
		);
	};
}

export default withRecoil(ChestPrize);
